import { cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import FormSection from './FormSection';
import LargeCheckBox from './LargeCheckBox';
import Tablet from './Tablet';
import { Body3, Title1 } from '../../../../../common/typography/Typography';
import validateYupSchema from '../../../../../common/utils/validateYupSchema';
import { DevicesConfig } from '../../../SelfServeFormView/SelfServeSignUpForm/types';
import mobileDataImage from '../../../images/mobile-data.png';
import printerImage from '../../../images/printer.png';
import { DevicePreferences, DevicePreferencesSchema } from '../validation';

type DeviceInfoProps = {
  title: string;
  price: string;
  description: string;
};

const FormSectionWrapper = styled.div`
  margin-block-end: 16px;
`;

const DeviceInfoRowWrapper = styled.div`
  > div:nth-child(1) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-block-end: 4px;
  }

  > div:nth-child(2) {
    font-weight: 400;
    line-height: 20px;
    color: ${cssThemedValue('textSecondary')};
  }
`;

const NeedDeviceCheckBox = styled(LargeCheckBox)`
  margin: 16px 0 8px;
`;

const DeviceInfo = ({ title, price, description }: DeviceInfoProps) => (
  <DeviceInfoRowWrapper>
    <div>
      <Body3>{title}</Body3>
      <Body3>{price}</Body3>
    </div>
    <div>
      <Body3>{description}</Body3>
    </div>
  </DeviceInfoRowWrapper>
);

type DeviceSelectionFormProps = {
  formId: string;
  devicePreferences: DevicePreferences;
  deviceConfig: DevicesConfig;
  handleSubmit: (values: DevicePreferences) => Promise<void | { [key: string]: string | Error }>;
};

const DevicePreferencesForm = ({ handleSubmit, formId, devicePreferences, deviceConfig }: DeviceSelectionFormProps) => {
  return (
    <Form<DevicePreferences>
      initialValues={{
        needsPrinter: devicePreferences.needsPrinter,
        needsMobileDataPackage: devicePreferences.needsMobileDataPackage,
      }}
      onSubmit={handleSubmit}
      validate={validateYupSchema(DevicePreferencesSchema)}
    >
      {({ handleSubmit }) => (
        <form id={formId} onSubmit={handleSubmit}>
          {deviceConfig.tabletConfiguration && (
            <FormSection>
              <Tablet
                style={{
                  width: '24px',
                  marginBottom: '8px',
                }}
              />
              <DeviceInfo
                title="Wolt tablet"
                price={`${deviceConfig.tabletConfiguration.tabletCost}€`}
                description="We’ll send you your very own Wolt tablet on which you’ll receive and handle your Wolt orders easily."
              />
            </FormSection>
          )}
          {(deviceConfig.printerOptionConfiguration || deviceConfig.mobileDataOption) && (
            <Title1
              style={{
                display: 'block',
                marginTop: '24px',
                marginBottom: '16px',
              }}
            >
              Want to be even more efficient?
            </Title1>
          )}
          {deviceConfig.printerOptionConfiguration && (
            <FormSectionWrapper>
              <FormSection>
                {/* TODO: use CDN images after https://github.com/creditornot/merchant-admin/pull/5558/files merged*/}
                <img src={printerImage} alt="printer banner" />
                <Field<boolean> name="needsPrinter" type="checkbox">
                  {field => <NeedDeviceCheckBox checked={field.input.checked} onChange={field.input.onChange} />}
                </Field>
                <DeviceInfo
                  title="Buy Wolt printer"
                  price={`${deviceConfig.printerOptionConfiguration.cost}€`}
                  description="Get printouts of your accepted orders and hand them over to the prep station. They also come in handy when packing the orders to ensure that nothing is lef out."
                />
              </FormSection>
            </FormSectionWrapper>
          )}
          {deviceConfig.mobileDataOption && (
            <FormSectionWrapper>
              <FormSection>
                {/* TODO: use CDN images after https://github.com/creditornot/merchant-admin/pull/5558/files merged*/}
                <img src={mobileDataImage} alt="mobile data banner" />
                <Field<boolean> name="needsMobileDataPackage" type="checkbox">
                  {field => <NeedDeviceCheckBox checked={field.input.checked} onChange={field.input.onChange} />}
                </Field>
                <DeviceInfo
                  title="Add mobile data"
                  price={`${deviceConfig.mobileDataOption.cost}€`}
                  description="Ensure you’re always connected by including a SIM card with mobile data to your tablet."
                />
              </FormSection>
            </FormSectionWrapper>
          )}
        </form>
      )}
    </Form>
  );
};

export default DevicePreferencesForm;
